import { useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  PenTool,
  BookOpen,
  TrendingUp,
  Network,
  Trophy,
  Library,
  Home,
  BookMarked,
  LogOut,
  Mic,
  Settings,
  Briefcase,
  Building2,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { auth } from '../../services/auth.js';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const navItems: { to: string; icon: LucideIcon; label: string }[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/canvas', icon: PenTool, label: 'Visuales' },
  { to: '/projects', icon: Briefcase, label: 'Proyectos' },
  { to: '/companies', icon: Building2, label: 'Empresas' },
  { to: '/courses', icon: BookOpen, label: 'Cursos' },
  { to: '/podcasts', icon: Mic, label: 'Podcasts' },
  { to: '/library', icon: Library, label: 'Biblioteca' },
  { to: '/progress', icon: TrendingUp, label: 'Progreso' },
  { to: '/graph', icon: Network, label: 'Grafo' },
  { to: '/badges', icon: Trophy, label: 'Logros' },
  { to: '/settings', icon: Settings, label: 'Configuración' },
];

export function Sidebar({ open, onToggle }: SidebarProps) {
  const userEmail = useMemo(() => auth.getUserEmail(), []);
  const avatarLetter = userEmail ? userEmail[0].toUpperCase() : '?';
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  useEffect(() => {
    if (!open) return;
    const openedOnMobile = window.innerWidth < 768;
    const previousFocus = openedOnMobile ? (document.activeElement as HTMLElement | null) : null;
    if (openedOnMobile) closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && window.innerWidth < 768) onToggleRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  return (
    <>
      {/* Mobile overlay scrim */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={onToggle}
        />
      )}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64 text-[var(--xds-fg)] transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'invisible -translate-x-full md:visible'}
          md:relative md:z-auto md:translate-x-0 md:transition-all md:duration-300
          ${!open ? 'md:w-16' : 'md:w-64'}
          flex-shrink-0
        `}
        style={{ background: 'var(--xds-sidebar-bg)' }}
      >
        {/* Gradient accent line at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: 'var(--xd-gradient-top-accent)' }}
        />

        <div className="flex h-full flex-col">
          {/* Logo area */}
          <div className={`flex h-16 items-center gap-3 ${open ? 'px-5' : 'justify-center px-0'}`}>
            <div
              className="logo-lockup relative flex-shrink-0"
              role="img"
              aria-label="xdface vault"
            >
              <img
                src="/logo-mark-transparent-light.svg"
                alt=""
                className="logo-light relative h-9 w-9"
              />
              <img
                src="/logo-mark-transparent-light.svg"
                alt=""
                className="logo-sepia relative h-9 w-9"
              />
              <img
                src="/logo-mark-transparent-dark.svg"
                alt=""
                className="logo-dark relative h-9 w-9"
              />
            </div>
            {open && (
              <div>
                <span className="vault-gradient-text text-[24px] font-bold leading-none tracking-tight">
                  vault
                </span>
                <span className="block text-[10px] text-[var(--xds-fg3)] font-medium tracking-widest uppercase leading-none">
                  xdface
                </span>
              </div>
            )}
            {open && (
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onToggle}
                aria-label="Cerrar menú"
                className="ml-auto rounded-lg p-2 text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] md:hidden"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <motion.nav
            aria-label="Navegación principal"
            className={`min-h-0 flex-1 overflow-y-auto py-3 ${open ? 'px-3' : 'px-2'}`}
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.04 } },
            }}
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.to}
                  variants={{
                    hidden: { opacity: 0, x: -8 },
                    show: { opacity: 1, x: 0, transition: { duration: 0.2 } },
                  }}
                >
                  <NavLink
                    to={item.to}
                    aria-label={item.label}
                    end={item.to === '/'}
                    title={!open ? item.label : undefined}
                    onClick={() => {
                      if (window.innerWidth < 768) onToggle();
                    }}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 mb-0.5 ${
                        isActive
                          ? 'text-[var(--xds-fg)] shadow-lg'
                          : 'text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] hover:text-[var(--xds-fg2)]'
                      } ${!open ? 'justify-center px-0 rounded-xl' : ''}`
                    }
                    style={({ isActive }) =>
                      isActive
                        ? {
                            background:
                              'linear-gradient(135deg, color-mix(in srgb, var(--xd-action) 25%, transparent), color-mix(in srgb, var(--xd-action-end) 18%, transparent))',
                            boxShadow:
                              '0 0 0 1px color-mix(in srgb, var(--xd-action) 25%, transparent), 0 2px 12px color-mix(in srgb, var(--xd-action) 15%, transparent)',
                          }
                        : {}
                    }
                  >
                    <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                    {open && <span>{item.label}</span>}
                  </NavLink>
                </motion.div>
              );
            })}
          </motion.nav>

          {/* Bottom separator with gradient */}
          <div
            className="mx-3 h-px"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--xds-sidebar-sep), transparent)',
            }}
          />

          {/* Apps navigation */}
          <div className="px-2 py-2">
            <a
              href="https://xdface.net"
              aria-label="Inicio — xdface.net"
              target="_blank"
              rel="noopener noreferrer"
              title={!open ? 'Inicio — xdface.net' : undefined}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 text-xs text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] hover:text-[var(--xds-fg2)] transition-colors duration-150 ${!open ? 'justify-center px-0' : ''}`}
            >
              <Home className="h-[14px] w-[14px] flex-shrink-0" />
              {open && <span>Inicio</span>}
            </a>
            <a
              href="https://library.xdface.net"
              aria-label="Biblioteca de xdface.net"
              target="_blank"
              rel="noopener noreferrer"
              title={!open ? 'Biblioteca' : undefined}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 text-xs text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] hover:text-[var(--xds-fg2)] transition-colors duration-150 ${!open ? 'justify-center px-0' : ''}`}
            >
              <BookMarked className="h-[14px] w-[14px] flex-shrink-0" />
              {open && <span>Biblioteca</span>}
            </a>
          </div>

          {/* Bottom separator with gradient */}
          <div
            className="mx-3 h-px"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--xds-sidebar-sep), transparent)',
            }}
          />

          {/* User info + logout */}
          <div className="px-2 pb-4 pt-2">
            <div
              className={`flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[var(--xds-gl)] transition-colors cursor-default ${!open ? 'justify-center' : ''}`}
            >
              <div
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-[var(--xd-action-fg)]"
                style={{ background: 'var(--xd-action)' }}
              >
                {avatarLetter}
              </div>
              {open && (
                <span className="truncate text-xs text-[var(--xds-fg3)]">
                  {userEmail ?? 'Usuario'}
                </span>
              )}
            </div>
            <button
              onClick={() => auth.logout()}
              aria-label="Cerrar sesión"
              title={!open ? 'Cerrar sesión' : undefined}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-xs text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] hover:text-xd-error transition-colors duration-150 ${!open ? 'justify-center px-0' : ''}`}
            >
              <LogOut className="h-[14px] w-[14px] flex-shrink-0" />
              {open && <span>Cerrar sesión</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
